import React from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Download, Share2, Sparkles } from 'lucide-react';
import { SectionCard } from '../common/SectionCard';
import { Button } from '../ui/Button';
import { Label } from '../ui/Label';
import { Skeleton } from '../ui/Skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { ReportDocument } from './ReportDocument';
import { usePlatform } from '../../contexts/PlatformContext';
import { useAiRun } from '../../hooks/useAiRun';
import { buildReport, reportTypeLabels } from '../../utils/reportBuilder';
import { currentOfficer } from '../../data/officers';
import type { GeneratedReport, ReportType } from '../../types/ai';
const RUN_STAGES = ['Assembling records in scope', 'Recomputing weighted scores', 'Evaluating gate status and risk flags', 'Drafting findings and recommendations', 'Formatting the report document'];
const periods = ['August 2026', 'Q3 2026', 'FY 2026 to date', 'Last 12 months'];
const audiences = ['SDU Director', 'Federal Alignment Committee', 'Delivery Assurance Board', 'Outreach & Ecosystem Team'];
export function ReportGenerator() {
  const {
    applications,
    addReport
  } = usePlatform();
  const [type, setType] = React.useState<ReportType>('portfolio-status');
  const [scope, setScope] = React.useState('all-mega-major');
  const [period, setPeriod] = React.useState(periods[1]);
  const [audience, setAudience] = React.useState(audiences[0]);
  const [report, setReport] = React.useState<GeneratedReport | null>(null);
  const run = useAiRun({
    stages: RUN_STAGES,
    stageDuration: 620,
    failureRate: 0.12
  });
  const scopeOptions = React.useMemo(() => [{
    id: 'all-mega-major',
    label: 'All Mega & Major records'
  }, {
    id: 'all-international',
    label: 'All International records'
  }, ...applications.map(record => ({
    id: record.id,
    label: `${record.ref} — ${record.eventName}`
  }))], [applications]);
  const scopeRecords = React.useMemo(() => {
    if (scope === 'all-mega-major') return applications.filter(record => record.track !== 'international');
    if (scope === 'all-international') return applications.filter(record => record.track === 'international');
    return applications.filter(record => record.id === scope);
  }, [applications, scope]);
  React.useEffect(() => {
    if (run.state === 'complete' && !report) {
      const built = buildReport({
        type,
        scopeRecords,
        scopeLabel: scopeOptions.find(option => option.id === scope)?.label ?? 'Selected scope',
        period,
        audience,
        author: `AI Report Generator · requested by ${currentOfficer.name}`
      });
      setReport(built);
      addReport(built);
      toast.success('Report generated', {
        description: built.title
      });
    }
  }, [run.state, report, type, scopeRecords, scopeOptions, scope, period, audience, addReport]);
  function generate() {
    setReport(null);
    run.start();
  }
  return <div className="grid gap-4 xl:grid-cols-[340px_1fr]" data-id="element-204" data-mp="22acwv9tvqbhh">
      <div className="space-y-4" data-id="element-205" data-mp="278xs0uu9y4z2">
        <SectionCard title="Generate a report" description="Reports are assembled from live record state, gate status and weighted scores." data-id="element-206" data-mp="277wbiauhoq9y">
          <div className="space-y-4" data-id="element-207" data-mp="1xhtcnu0osi3p">
            <div className="space-y-1.5" data-id="element-208" data-mp="1xgrxpegjvbgp">
              <Label htmlFor="report-type" data-id="element-209" data-mp="1jity5f6w1jl9">Report type</Label>
              <Select value={type} onValueChange={value => setType(value as ReportType)} data-id="element-210" data-mp="1jlg5bdpv9x2y">
                <SelectTrigger id="report-type" data-id="element-211" data-mp="1og3k2kv2jd4c">
                  <SelectValue data-id="element-212" data-mp="1aj6s2g37rplv" />
                </SelectTrigger>
                <SelectContent data-id="element-213" data-mp="1ek0jhjfv7mji">
                  {(Object.keys(reportTypeLabels) as ReportType[]).map(key => <SelectItem key={key} value={key} data-id="element-214" data-mp="34vwpv8wpdiyj">
                      {reportTypeLabels[key]}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5" data-id="element-215" data-mp="2k6cbqe5jysas">
              <Label htmlFor="report-scope" data-id="element-216" data-mp="2k518cfg0rmk7">Scope</Label>
              <Select value={scope} onValueChange={setScope} data-id="element-217" data-mp="2f9c25t9hv7ex">
                <SelectTrigger id="report-scope" data-id="element-218" data-mp="2a8xuicbt1zhf">
                  <SelectValue data-id="element-219" data-mp="2a7msoi1x71sy" />
                </SelectTrigger>
                <SelectContent data-id="element-220" data-mp="25awcluq4867j">
                  {scopeOptions.map(option => <SelectItem key={option.id} value={option.id} data-id="element-221" data-mp="2189ifn5z2976">
                      {option.label}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground" data-id="element-222" data-mp="1ri6q7p6ocz9w">
                {scopeRecords.length} record{scopeRecords.length === 1 ? '' : 's'} in scope
              </p>
            </div>

            <div className="space-y-1.5" data-id="element-223" data-mp="1hl20xghj8nov">
              <Label htmlFor="report-period" data-id="element-224" data-mp="1hk0qne8cc587">Period</Label>
              <Select value={period} onValueChange={setPeriod} data-id="element-225" data-mp="1dif5z6pkemng">
                <SelectTrigger id="report-period" data-id="element-226" data-mp="1dlb2qrpof5ab">
                  <SelectValue data-id="element-227" data-mp="3p58wopdf906x" />
                </SelectTrigger>
                <SelectContent data-id="element-228" data-mp="3t7vv594lljd3">
                  {periods.map(option => <SelectItem key={option} value={option} data-id="element-229" data-mp="3t9yoo34ylumk">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5" data-id="element-230" data-mp="35iba8kovwc16">
              <Label htmlFor="report-audience" data-id="element-231" data-mp="35jmbacqy35ol">Audience</Label>
              <Select value={audience} onValueChange={setAudience} data-id="element-232" data-mp="3ah4le6gp7qy6">
                <SelectTrigger id="report-audience" data-id="element-233" data-mp="3afbfwuhtncrr">
                  <SelectValue data-id="element-234" data-mp="3fg7jt826ayx6" />
                </SelectTrigger>
                <SelectContent data-id="element-235" data-mp="3k77es7rqf6x1">
                  {audiences.map(option => <SelectItem key={option} value={option} data-id="element-236" data-mp="3k9adrhav4adw">
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <Button className="w-full" size="sm" onClick={generate} disabled={run.state === 'running' || scopeRecords.length === 0} data-id="element-237" data-mp="2jq74bwj9x4uj">
              <Sparkles className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-238" data-mp="2tg0dyrzuv9w5" />
              {run.state === 'running' ? 'Generating…' : 'Generate report'}
            </Button>
          </div>
        </SectionCard>

        {run.state === 'running' && <div className="rounded-sm bg-uae-green p-4 text-white" aria-live="polite" data-id="element-239" data-mp="20s4drdb2uzum">
            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/60" data-id="element-240" data-mp="1wqst7m7294ta">
              Generation run
            </p>
            <ol className="mt-2 space-y-1.5" data-id="element-241" data-mp="2anfrbsar210s">
              {RUN_STAGES.map((stageLabel, index) => <li key={stageLabel} className={`text-[12px] ${index < run.stageIndex ? 'text-white/50 line-through' : index === run.stageIndex ? 'text-white' : 'text-white/40'}`} data-id="element-242" data-mp="25sscym9gmmzx" data-mpi={index}>
                  {stageLabel}
                </li>)}
            </ol>
            <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-white/10" data-id="element-243" data-mp="ty6uycq02ast">
              <div className="h-full bg-white/60 transition-all duration-500" style={{
            width: `${run.progress}%`
          }} data-id="element-244" data-mp="yvgg3g6cw0au" />
            </div>
          </div>}
      </div>

      <div data-id="element-245" data-mp="1qm4ewlkk4tlc">
        {run.state === 'idle' && !report && <SectionCard title="No report generated yet" description="Choose a type, scope, period and audience, then generate. The document renders here with export and share actions." data-id="element-246" data-mp="1cw9bs48e91fj">
            <div className="space-y-3" data-id="element-247" data-mp="25kewiqzg6qfd">
              <Skeleton className="h-4 w-2/3" data-id="element-248" data-mp="25jdn0qy2ybzr" />
              <Skeleton className="h-3 w-full" data-id="element-249" data-mp="2abx0lnwrp74k" />
              <Skeleton className="h-3 w-11/12" data-id="element-250" data-mp="2acpzk2phisc5" />
              <Skeleton className="h-3 w-3/4" data-id="element-251" data-mp="a9nr01dqv6m5" />
              <Skeleton className="h-24 w-full" data-id="element-252" data-mp="aagoscuq6ls5" />
            </div>
          </SectionCard>}

        {run.state === 'running' && <SectionCard title="Drafting document" description={run.currentStage ?? ''} data-id="element-253" data-mp="j2qu9if9u14">
            <div className="space-y-3" data-id="element-254" data-mp="3rpnh3r0nkxx3">
              <Skeleton className="h-4 w-1/2" data-id="element-255" data-mp="3rqoqlr20tccp" />
              <Skeleton className="h-3 w-full" data-id="element-256" data-mp="3rnstg4xzyrpb" />
              <Skeleton className="h-3 w-10/12" data-id="element-257" data-mp="t6ynxfbwlnyf" />
              <Skeleton className="h-3 w-9/12" data-id="element-258" data-mp="t5njddamwc69" />
              <Skeleton className="h-28 w-full" data-id="element-259" data-mp="o9yhv4f20l7b" />
              <Skeleton className="h-3 w-8/12" data-id="element-260" data-mp="o73t50p0ce83" />
            </div>
          </SectionCard>}

        {run.state === 'error' && <SectionCard title="Generation failed" data-id="element-261" data-mp="f909k9xl1rix">
            <div className="flex items-start gap-3 rounded-sm border border-uae-red/30 bg-uae-red/5 px-3 py-3" data-id="element-262" data-mp="fabgujq8lhes">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-uae-red" aria-hidden="true" data-id="element-263" data-mp="2zyh9kpilrxxs" />
              <div data-id="element-264" data-mp="1fgf6exoj1tbi">
                <p className="text-[13px] font-medium text-uae-red" data-id="element-265" data-mp="1fgyn6b2fybny">
                  The generation service did not return a document
                </p>
                <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground" data-id="element-266" data-mp="1jfxprp0p9wc8">
                  No partial report was saved. Retry the run — the scope and configuration are
                  preserved.
                </p>
                <Button size="sm" variant="outline" className="mt-3" onClick={generate} data-id="element-267" data-mp="1y76m0gz3yfyc">
                  Retry generation
                </Button>
              </div>
            </div>
          </SectionCard>}

        {report && run.state === 'complete' && <div className="space-y-3" data-id="element-268" data-mp="275i9wb8lazn3">
            <div className="flex flex-wrap items-center justify-end gap-2" data-id="element-269" data-mp="2c77o1zr3ztfe">
              <Button variant="outline" size="sm" onClick={() => toast.success('Report shared', {
            description: `Sent to ${report.audience}.`
          })} data-id="element-270" data-mp="ctlek77119mr">
                <Share2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-271" data-mp="2scp7n6jnn6nr" />
                Share
              </Button>
              <Button size="sm" onClick={() => toast.success('Export prepared', {
            description: `${report.title}.pdf`
          })} data-id="element-272" data-mp="328s724n4gr70">
                <Download className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" data-id="element-273" data-mp="3b9qbup5dx2qn" />
                Export PDF
              </Button>
            </div>
            <ReportDocument report={report} data-id="element-274" data-mp="3g78mcjz1vq0r" />
          </div>}
      </div>
    </div>;
}